
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, DollarSign, Wallet, CreditCard, Coins, User, ArrowDownCircle, HelpCircle } from 'lucide-react';
import { Order, Driver, SystemConfig } from '../types';
import { calculateFinancials, getDriverSettlement } from '../services/financialService';
import { SmartNumberInput } from './SmartNumberInput';
import { BaseTooltip } from './shared/BaseTooltip';
import { AuditDictionary } from '../services/logistics/core/auditCalculations';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  orders: Order[];
  drivers: Driver[];
  config?: SystemConfig;
  onFinalizeDay: () => void;
  onAddSangria: (driverId: string, amount: number) => void;
}

export const CashFlowModal: React.FC<Props> = ({ isOpen, onClose, orders, drivers, config, onFinalizeDay, onAddSangria }) => {
  const [selectedDriverIdForSangria, setSelectedDriverIdForSangria] = useState<string | null>(null);
  const [sangriaAmount, setSangriaAmount] = useState(0);

  if (!isOpen) return null;

  const dummyConfig = config || { dailyFixedCost: 0, variableGasPerOrder: 0, driversCollectPayments: true } as any; 
  const financials = calculateFinancials(orders, drivers, dummyConfig);
  const doesCollect = dummyConfig.driversCollectPayments !== false;

  const handleSaveSangria = () => {
    if (selectedDriverIdForSangria && sangriaAmount > 0) {
        onAddSangria(selectedDriverIdForSangria, sangriaAmount);
        setSangriaAmount(0);
        setSelectedDriverIdForSangria(null);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-sal-gunmetal/80 backdrop-blur-sm"
        />

        <motion.div 
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          className="relative bg-sal-gunmetal-light w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] border border-white/10"
        >
          <div className="bg-sal-gunmetal text-white px-6 py-4 flex justify-between items-center shrink-0 border-b border-white/5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-sal-cyber-teal rounded-full text-sal-gunmetal shadow-lg">
                <Coins className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold tracking-tight">Fechamento do Caixa</h2>
                <div className="flex items-center gap-2 text-sm opacity-70 font-mono text-slate-400">
                    <span>{new Date().toLocaleDateString()}</span>
                    <span>•</span>
                    <BaseTooltip data={AuditDictionary.fleetProductivity(orders.length)}>
                        <span className="cursor-help">{orders.length} Entregas Concluídas</span>
                    </BaseTooltip>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          <div className="p-6 overflow-y-auto no-scrollbar flex-1 space-y-8 bg-sal-gunmetal-light">
             <div>
                 <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3 px-1">Resumo de Ganhos</h3>
                 <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <BaseTooltip data={AuditDictionary.revenueGross(financials.grossProducts, financials.grossFees)}>
                        <div className="bg-sal-gunmetal p-4 rounded-xl border border-white/5 shadow-sm flex flex-col justify-between h-full hover:border-sal-cyber-teal/30 transition-colors">
                            <div className="flex items-center gap-2 mb-2">
                            <DollarSign className="w-4 h-4 text-sal-cyber-teal" />
                            <span className="text-sm font-bold text-white uppercase">Vendas Totais</span>
                            </div>
                            <div className="text-3xl font-bold text-white font-mono">
                            R$ {financials.revenueTotal.toFixed(2)}
                            </div>
                        </div>
                    </BaseTooltip>

                    <BaseTooltip data={AuditDictionary.cashFlowPhysical()}>
                        <div className="bg-green-900/20 p-4 rounded-xl border border-green-900/40 shadow-sm flex flex-col justify-between h-full hover:bg-green-900/30 transition-colors">
                            <div className="flex items-center gap-2 mb-2">
                            <Wallet className="w-4 h-4 text-green-400" />
                            <span className="text-sm font-bold text-green-300 uppercase">Em Dinheiro</span>
                            </div>
                            <div className="text-3xl font-bold text-green-400 font-mono">
                            R$ {financials.revenueCash.toFixed(2)}
                            </div>
                            <div className="text-xs text-green-500 font-medium">
                                {doesCollect ? "A Receber" : "Entrada Hub"}
                            </div>
                        </div>
                    </BaseTooltip>

                    <BaseTooltip data={{ title: "Faturamento Digital", description: "Valores pagos antecipadamente ou via Pix.", type: 'financial' }}>
                        <div className="bg-blue-900/20 p-4 rounded-xl border border-blue-900/40 shadow-sm flex flex-col justify-between h-full hover:bg-blue-900/30 transition-colors">
                            <div className="flex items-center gap-2 mb-2">
                            <CreditCard className="w-4 h-4 text-blue-400" />
                            <span className="text-sm font-bold text-blue-300 uppercase">Pix / Online</span>
                            </div>
                            <div className="text-3xl font-bold text-blue-400 font-mono">
                            R$ {financials.revenueOnline.toFixed(2)}
                            </div>
                            <div className="text-xs text-blue-500 font-medium">Saldo em Conta</div>
                        </div>
                    </BaseTooltip>

                    <BaseTooltip data={{ title: "Faturamento Maquininha", description: "Total processado em cartões na entrega.", type: 'financial' }}>
                        <div className="bg-orange-900/20 p-4 rounded-xl border border-orange-900/40 shadow-sm flex flex-col justify-between h-full hover:bg-orange-900/30 transition-colors">
                            <div className="flex items-center gap-2 mb-2">
                            <CreditCard className="w-4 h-4 text-orange-400" />
                            <span className="text-sm font-bold text-orange-300 uppercase">Maquininha</span>
                            </div>
                            <div className="text-3xl font-bold text-orange-400 font-mono">
                            R$ {financials.revenueCard.toFixed(2)}
                            </div>
                            <div className="text-xs text-orange-500 font-medium">Conferir Comprovantes</div>
                        </div>
                    </BaseTooltip>
                 </div>
             </div>

             {doesCollect && (
                 <>
                 {!selectedDriverIdForSangria ? (
                     <button 
                        onClick={() => setSelectedDriverIdForSangria(drivers[0]?.id || '')}
                        className="w-full py-4 border-2 border-dashed border-white/20 rounded-xl flex items-center justify-center text-slate-400 font-bold hover:border-sal-cyber-teal hover:text-sal-cyber-teal hover:bg-sal-cyber-teal/5 transition-all gap-2"
                     >
                        <ArrowDownCircle className="w-5 h-5" />
                        Recolher Dinheiro do Motoboy (Sangria)
                     </button>
                 ) : (
                     <motion.div 
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        className="bg-yellow-900/20 border border-yellow-700/30 rounded-xl p-4 overflow-hidden"
                     >
                        <div className="flex justify-between items-center mb-4">
                            <h4 className="font-bold text-yellow-500 flex items-center gap-2">
                                <ArrowDownCircle className="w-5 h-5" /> Nova Retirada
                            </h4>
                            <button onClick={() => setSelectedDriverIdForSangria(null)} className="p-1 hover:bg-yellow-900/30 rounded text-yellow-600"><X className="w-4 h-4"/></button>
                        </div>
                        <div className="flex flex-col md:flex-row gap-4 items-end">
                            <div className="flex-1 w-full">
                                <label className="text-sm font-bold text-yellow-600 uppercase mb-1 block">Entregador</label>
                                <select 
                                    value={selectedDriverIdForSangria || ''}
                                    onChange={(e) => setSelectedDriverIdForSangria(e.target.value)}
                                    className="w-full p-2.5 rounded-lg border border-transparent bg-sal-gunmetal text-base font-bold text-white focus:outline-none focus:ring-1 focus:ring-yellow-500"
                                >
                                    {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                </select>
                            </div>
                            <div className="flex-1 w-full">
                                <label className="text-sm font-bold text-yellow-600 uppercase mb-1 block">Valor Recolhido (R$)</label>
                                <input 
                                    type="number"
                                    value={sangriaAmount || ''}
                                    onChange={e => setSangriaAmount(parseFloat(e.target.value) || 0)}
                                    className="w-full p-2.5 rounded-lg border border-transparent bg-sal-gunmetal text-base font-bold font-mono text-white focus:outline-none focus:ring-1 focus:ring-yellow-500"
                                    placeholder="0.00"
                                />
                            </div>
                            <button 
                                onClick={handleSaveSangria}
                                disabled={sangriaAmount <= 0}
                                className="w-full md:w-auto px-6 py-2.5 bg-yellow-600 text-white font-bold rounded-lg hover:bg-yellow-700 disabled:opacity-50 transition-colors shadow-sm"
                            >
                                Confirmar Sangria
                            </button>
                        </div>
                     </motion.div>
                 )}
                 </>
             )}

             <div>
                <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3 px-1">
                    {doesCollect ? "Acerto com a Equipe" : "Produtividade da Frota"}
                </h3>
                <div className="space-y-3">
                   {drivers.map((driver) => {
                      const settlement = getDriverSettlement(driver, orders);
                      if (settlement.deliveriesCount === 0 && (!driver.cashReturns || driver.cashReturns.length === 0)) return null;

                      return (
                          <div key={driver.id} className="bg-sal-gunmetal border border-white/5 rounded-xl p-4 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4 hover:border-white/20 transition-colors">
                             <div className="flex items-center gap-3 w-full md:w-auto">
                                <div className="w-10 h-10 bg-white/10 text-white rounded-full flex items-center justify-center font-bold text-base shadow-md">
                                   <User className="w-5 h-5" />
                                </div>
                                <div>
                                   <div className="font-bold text-white text-lg">{driver.name}</div>
                                   <BaseTooltip data={AuditDictionary.fleetProductivity(settlement.deliveriesCount)}>
                                        <div className="text-sm text-slate-400 font-medium cursor-help">
                                            {settlement.deliveriesCount} entregas finalizadas
                                        </div>
                                   </BaseTooltip>
                                </div>
                             </div>

                             {doesCollect ? (
                                <>
                                    <div className="flex items-center gap-6 text-sm text-slate-400 w-full md:w-auto justify-between md:justify-center bg-black/20 p-2 rounded-lg md:bg-transparent md:p-0">
                                        <BaseTooltip data={{ title: "Volume Dinheiro", description: "Valor total em cédulas.", formula: "Sum(Pedidos Dinheiro)", type: 'financial' }}>
                                            <div className="text-center">
                                                <span className="block font-bold text-slate-500 uppercase text-xs flex items-center gap-1">Dinheiro <HelpCircle className="w-2.5 h-2.5 opacity-30"/></span>
                                                <span className="font-mono font-bold text-slate-300">R$ {settlement.cashToCollect.toFixed(2)}</span>
                                            </div>
                                        </BaseTooltip>
                                        <BaseTooltip data={{ title: "Total Sangrias", description: "Valores já recolhidos do motoboy.", type: 'financial' }}>
                                            <div className="text-center">
                                                <span className="block font-bold text-slate-500 uppercase text-xs flex items-center gap-1">Recolhido <HelpCircle className="w-2.5 h-2.5 opacity-30"/></span>
                                                <span className="font-mono font-bold text-red-400">- R$ {settlement.totalSangria.toFixed(2)}</span>
                                            </div>
                                        </BaseTooltip>
                                    </div>
                                    
                                    <BaseTooltip data={AuditDictionary.driverSettlement(settlement.cashToCollect, settlement.totalSangria)}>
                                        <div className="text-right min-w-[120px] cursor-help">
                                            <div className="text-xs text-slate-400 uppercase font-bold mb-1">Dívida de Caixa</div>
                                            <div className={`font-mono text-2xl font-bold px-3 py-1 rounded-lg border ${settlement.netCashDebt > 0 ? 'bg-red-900/20 text-red-400 border-red-900/40' : 'bg-green-900/20 text-green-400 border-green-900/40'}`}>
                                            R$ {settlement.netCashDebt.toFixed(2)}
                                            </div>
                                        </div>
                                    </BaseTooltip>
                                </>
                             ) : (
                                 <BaseTooltip data={{ title: "Volume Bruto", description: "Faturamento total das missões deste parceiro.", type: 'financial' }}>
                                     <div className="text-right">
                                         <div className="text-xs font-bold text-slate-500 uppercase">Total Produzido</div>
                                         <div className="text-slate-300 font-mono font-bold">R$ {settlement.totalProduced.toFixed(2)}</div>
                                     </div>
                                 </BaseTooltip>
                             )}
                          </div>
                      );
                   })}
                </div>
             </div>
          </div>

          <div className="p-4 border-t border-white/5 bg-sal-gunmetal text-center shrink-0 flex justify-end gap-3">
             <button onClick={onClose} className="px-6 py-3 font-bold text-slate-400 hover:bg-white/5 rounded-xl transition-colors">Voltar</button>
             <button 
               onClick={onFinalizeDay}
               className="px-8 py-3 bg-sal-cyber-teal text-sal-gunmetal font-bold rounded-xl hover:brightness-110 transition-all shadow-lg"
             >
                Finalizar e Arquivar Dia
             </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
